'use client';

import { useEffect, useState } from 'react';
import { BaseCarousel } from 'components/shared/Carousel/BaseCarousel';
import { carouselClassNames } from 'components/shared/Card/styles';
import Media from 'components/shared/Media/Media';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import Link from 'components/shared/Link/Link';
import { ArticleTag } from './ArticleTag';
import { filledTagClassName } from './filledTagClassName';
import { getRichTextValue } from 'utils/formatters';
import { useAutoplayPlugin } from 'hooks/useAutoplayPlugin';
import { RelatedArticleProps } from './types';
import { ArticleGridProps } from './types';
import { resolveArticleTags } from './utils';

interface ArticleGridSpotlightProps extends Pick<ArticleGridProps, 'color'> {
  articles: RelatedArticleProps[];
  includeCategoryLinks?: boolean;
}

const AUTOPLAY_DELAY = 5000;

const ArticleGridSpotlight = ({ color, articles, includeCategoryLinks }: ArticleGridSpotlightProps) => {
  const [isClient, setIsClient] = useState(false);
  const autoplayPlugin = useAutoplayPlugin({
    delay: AUTOPLAY_DELAY,
    stopOnMouseEnter: true,
    stopOnFocusIn: true,
    stopOnInteraction: false,
    rootNode: (emblaRoot: HTMLElement) => emblaRoot.parentElement,
  });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydration check, only runs once on mount
    setIsClient(true);
  }, []);

  const slides = (
    <div className="grid grid-flow-col auto-cols-[100%] gap-x-8">
      {articles.map((article, i) => (
        <SpotlightCard key={i} article={article} includeCategoryLinks={includeCategoryLinks} />
      ))}
    </div>
  );

  if (!isClient) return slides;

  return (
    <BaseCarousel
      totalItems={articles.length}
      carouselClassName={carouselClassNames(color)}
      carouselPaginationClassName="mt-8"
      carouselContentClassName="w-full"
      showPaginationThreshold={1}
      carouselOpts={{ align: 'start', slidesToScroll: 1, containScroll: false, loop: articles.length > 1 }}
      plugins={autoplayPlugin && articles.length > 1 ? [autoplayPlugin] : []}
      isTimed={!!autoplayPlugin && articles.length > 1}
      timeDuration={AUTOPLAY_DELAY / 1000}>
      {slides}
    </BaseCarousel>
  );
};

interface SpotlightCardProps {
  article: RelatedArticleProps;
  includeCategoryLinks?: boolean;
}

const SpotlightCard = ({ article, includeCategoryLinks }: SpotlightCardProps) => {
  const { articleImage, articleTitle, slug, href } = article;
  const plainTitle = getRichTextValue(articleTitle);
  const internalPage = href ? undefined : { slug };
  const linkText = 'Read more';
  const tags = resolveArticleTags(article, includeCategoryLinks);

  return (
    <div className="bg-bg-primary rounded-lg overflow-hidden px-7 py-8 sm:px-14 sm:py-16">
      <div className="grid sm:grid-cols-2 gap-8 h-full">
        <div className="flex flex-col justify-between space-y-2.5 order-2 sm:order-1">
          <div className="space-y-4">
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-1 font-serif text-eyebrow font-normal">
                {tags.map((tag, i) =>
                  tag.filled ? (
                    <ArticleTag key={`${tag.label}-${i}`} label={tag.label} className={filledTagClassName()} />
                  ) : tag.href ? (
                    <Link key={`${tag.label}-${i}`} href={tag.href} className="hover:underline">
                      {tag.label}
                    </Link>
                  ) : (
                    <span key={`${tag.label}-${i}`}>{tag.label}</span>
                  ),
                )}
              </div>
            )}
            <div className="font-sans text-heading-lg">
              <Link internalPage={internalPage} href={href} className="text-heading-lg">
                <RichTextHeadline level="h3" headline={articleTitle} />
              </Link>
            </div>
          </div>
          <div className="font-sans text-label-saans underline font-medium">
            <Link
              internalPage={internalPage}
              href={href}
              className="underline"
              aria-label={`${linkText}: ${plainTitle}`}>
              {linkText}
            </Link>
          </div>
        </div>
        {articleImage && (
          <div className="order-1 sm:order-2">
            <Media
              media={articleImage}
              sizes="(max-width: 750px) 100vw, 50vw"
              className="h-full w-full rounded-lg object-cover"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleGridSpotlight;
